import { Box } from "@chakra-ui/react";
import { useRef } from "react";

import { GPDVolumeSeriesData } from "@nens/brostar/uploadapi-client";
import { Plot } from "@nens/plotly";
import { useRect } from "@nens/react/hooks";

interface GpdVolumeSeriesChartProps {
  volumeSeries: GPDVolumeSeriesData[];
}

const BAR_WIDTH_PX = 60;
const CHART_FIXED_OVERHEAD = 80; // y-axis label area + margins
const TARGET_BARS_VISIBLE = 12;

// Color palette based on waterInOut type and temperature
function getBarColor(waterInOut: string, temperature?: string): string {
  const isOnttrokken = waterInOut === "onttrokken";

  if (temperature === "warm") {
    return isOnttrokken ? "rgb(180,100,100)" : "rgb(220,160,160)";
  } else if (temperature === "koud") {
    return isOnttrokken ? "rgb(100,130,180)" : "rgb(160,180,220)";
  } else {
    // default / onbekend
    return isOnttrokken ? "rgb(128,128,128)" : "rgb(200,200,200)";
  }
}

export function GpdVolumeSeriesChart({ volumeSeries }: GpdVolumeSeriesChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth } = useRect(containerRef);

  if (volumeSeries.length === 0) {
    return null;
  }

  // Separate traces for onttrokken and ingebracht
  const onttrokkenData = volumeSeries.map((row, i) => ({
    index: i,
    volume: row.waterInOut === "onttrokken" ? row.volume : 0,
    temperature: row.temperature,
    waterInOut: row.waterInOut,
    period: `${row.beginDate} – ${row.endDate}`,
  }));

  const ingebrachtData = volumeSeries.map((row, i) => ({
    index: i,
    volume: row.waterInOut === "ingebracht" ? -row.volume : 0,
    temperature: row.temperature,
    waterInOut: row.waterInOut,
    period: `${row.beginDate} – ${row.endDate}`,
  }));

  // Find max absolute volume for symmetric Y-axis
  const maxVolume = Math.max(...volumeSeries.map((row) => Math.abs(row.volume) || 0));

  // X-axis labels from first row (period names)
  const xLabels = volumeSeries.map((row) => `${row.beginDate} – ${row.endDate}`);

  // Build Plotly traces
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const traces: any[] = [
    {
      x: xLabels,
      y: onttrokkenData.map((d) => d.volume),
      name: "Onttrokken",
      type: "bar",
      marker: {
        color: onttrokkenData.map((d) => getBarColor("onttrokken", d.temperature)),
        pattern: { shape: "x" }, // cross-hatch pattern
      },
      hovertemplate: "<b>Onttrokken</b><br>Periode: %{x}<br>Volume: %{y} m³<extra></extra>",
    },
    {
      x: xLabels,
      y: ingebrachtData.map((d) => d.volume),
      name: "Ingebracht",
      type: "bar",
      marker: {
        color: ingebrachtData.map((d) => getBarColor("ingebracht", d.temperature)),
      },
      hovertemplate: "<b>Ingebracht</b><br>Periode: %{x}<br>Volume: %{y} m³<extra></extra>",
    },
  ];

  // Calculate chart width: min width for 12 bars or actual bar count
  const minWidth = TARGET_BARS_VISIBLE * BAR_WIDTH_PX + CHART_FIXED_OVERHEAD;
  const actualWidth = volumeSeries.length * BAR_WIDTH_PX + CHART_FIXED_OVERHEAD;
  const chartWidth = Math.max(containerWidth, minWidth, actualWidth);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const layout: any = {
    title: "",
    xaxis: {
      title: { text: "Periode" },
      tickfont: { size: 9 },
      tickangle: -15,
    },
    yaxis: {
      title: { text: "Volume in m³" },
      range: [-maxVolume * 1.1, maxVolume * 1.1], // 10% padding
    },
    barmode: "overlay",
    margin: {
      l: 60,
      r: 40,
      t: 40,
      b: 100, // extra bottom for rotated labels
    },
    width: chartWidth,
    height: 350,
    showlegend: true,
    hovermode: "closest",
  };

  const config = {
    displayModeBar: false,
    responsive: false,
  };

  return (
    <Box ref={containerRef} mt={4} overflowX="auto" overflowY="hidden">
      <Plot data={traces} layout={layout} config={config} />
    </Box>
  );
}
